/**
 * Wizard Command
 *
 * Spawn a new terminal for interactive key entry.
 * Enables agent-human handoff for credential rotation.
 */

import * as fs from 'fs-extra';
import * as path from 'path';
import * as os from 'os';
import * as crypto from 'crypto';
import { CredentialService } from '../core/service';
import * as output from '../utils/output';
import { spawnTerminal, waitForSignalFile, findTerminal, isValidPath, isHeadless } from '../wizard/terminal';

interface WizardOptions {
  keys: string;
  reason?: string;
  timeout?: string;
  noWait?: boolean;
  inline?: boolean;
}

/**
 * Generate secure paths for wizard communication
 * Uses cryptographically random suffix instead of predictable PID
 */
async function getWizardPaths(): Promise<{ tmpDir: string; configPath: string; signalPath: string }> {
  // Use cryptographically secure random suffix
  const randomSuffix = crypto.randomBytes(16).toString('hex');
  const tmpDir = await fs.mkdtemp(path.join(os.tmpdir(), 'secretsage-wizard-'));

  // Set restrictive permissions on temp directory
  await fs.chmod(tmpDir, 0o700);

  return {
    tmpDir,
    configPath: path.join(tmpDir, `config-${randomSuffix}.json`),
    signalPath: path.join(tmpDir, `signal-${randomSuffix}.done`),
  };
}

export async function wizardCommand(options: WizardOptions): Promise<void> {
  const service = new CredentialService();

  // Check vault exists
  const vaults = await service.hasVault();
  if (!vaults.local && !vaults.global) {
    output.error('No vault found. Run `secretsage init` first.');
    process.exit(1);
  }

  // Parse keys
  const keys = options.keys.split(',').map((k) => k.trim()).filter(Boolean);
  if (keys.length === 0) {
    output.error('No keys specified. Use --keys KEY1,KEY2,...');
    process.exit(1);
  }

  // Determine if we should run inline
  const headless = isHeadless();
  const runInline = options.inline || headless;

  if (runInline) {
    // Run wizard UI directly in current terminal
    if (headless && !options.inline) {
      output.warning('Headless environment detected (no DISPLAY). Running inline.');
    }
    output.info(`Starting inline wizard for ${keys.length} key(s)...`);
    if (options.reason) {
      output.keyValue('Reason', options.reason);
    }
    output.divider();

    // Import and run wizard UI directly
    const { runWizardUI } = await import('../wizard/ui');
    await runWizardUI({
      keys,
      reason: options.reason,
      signalPath: undefined, // No signal file needed for inline mode
    });
    return;
  }

  // Check terminal availability for GUI mode
  const terminal = await findTerminal();
  if (!terminal) {
    output.error('No terminal emulator found.');
    output.info('');
    output.info('Options:');
    output.info('  1. Install: gnome-terminal, konsole, xterm, or xfce4-terminal');
    output.info('  2. Use inline mode: secretsage wizard --keys KEY1,KEY2 --inline');
    output.info('  3. Add keys manually: secretsage add KEY1 --value "..."');
    process.exit(1);
  }

  // Generate secure paths
  const { tmpDir, configPath, signalPath } = await getWizardPaths();

  // Write config for wizard UI with secure permissions
  const config = {
    keys,
    reason: options.reason,
    signalPath,
  };
  await fs.writeFile(configPath, JSON.stringify(config, null, 2), { mode: 0o600 });

  output.info(`Starting wizard for ${keys.length} key(s)...`);
  if (options.reason) {
    output.keyValue('Reason', options.reason);
  }
  output.divider();

  // Spawn wizard in new terminal
  try {
    // Get the path to the CLI and the running node binary
    // Use process.argv[1] since __dirname is wrong in bundled code
    const cliPath = process.argv[1]; // The CLI script that's currently running
    const nodePath = process.execPath; // Use the same node that's running us

    await spawnTerminal(nodePath, [cliPath, 'wizard-ui', '--config', configPath]);

    output.info('Wizard opened in new terminal window.');

    if (options.noWait) {
      output.info('Running in no-wait mode. Check signal file when done:');
      console.log(`  ${signalPath}`);
      return;
    }

    // Wait for completion
    output.info('Waiting for wizard to complete...');
    output.info('(Press Ctrl+C to stop waiting)');

    const timeoutMs = options.timeout ? parseInt(options.timeout, 10) * 1000 : 300000;
    const result = await waitForSignalFile(signalPath, timeoutMs);

    // Clean up entire temp directory
    await fs.remove(tmpDir);

    if (result.success) {
      output.success('Wizard completed successfully!');
      output.info('Keys have been saved to the vault.');
      output.info('Run `secretsage grant --all` to update .env');
    } else {
      output.warning(`Wizard did not complete: ${result.error}`);
      process.exit(1);
    }

  } catch (error) {
    // Clean up entire temp directory on error
    await fs.remove(tmpDir).catch(() => {});

    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
    output.error(`Failed to start wizard: ${errorMsg}`);
    output.info('');
    output.info('Try running with --inline flag:');
    output.info(`  secretsage wizard --keys "${options.keys}" --inline`);
    process.exit(1);
  }
}

/**
 * Wizard UI command - runs in the spawned terminal
 */
interface WizardUIOptions {
  config: string;
}

export async function wizardUICommand(options: WizardUIOptions): Promise<void> {
  // Import dynamically to avoid loading UI deps in main process
  const { runWizardUI } = await import('../wizard/ui');

  if (!options.config) {
    console.error('Error: --config is required');
    process.exit(1);
  }

  // Validate config path is within system temp directory (security)
  const resolvedPath = path.resolve(options.config);
  if (!isValidPath(resolvedPath, [os.tmpdir()])) {
    console.error('Error: Config file must be in system temp directory');
    process.exit(1);
  }

  try {
    const configContent = await fs.readFile(options.config, 'utf8');
    const config = JSON.parse(configContent);

    // Validate config structure
    if (!config.keys || !Array.isArray(config.keys)) {
      throw new Error('Invalid config: missing keys array');
    }
    if (!config.signalPath || typeof config.signalPath !== 'string') {
      throw new Error('Invalid config: missing signalPath');
    }

    await runWizardUI(config);

  } catch (error) {
    console.error('Error: Failed to run wizard');
    process.exit(1);
  }
}
